<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import AllFiltersItem from './AllFiltersItem.vue';
    import EchoButton from '../EchoButton.vue';
    import { RelativeRangePreset, type RelativeDateRangeDto } from '@/js/services/api/filter/types';
    import { computed } from 'vue';
    import SelectInput from '../SelectInput.vue';
    import { HourglassIcon } from '@lucide/vue';

    const props = defineProps<{
        value: RelativeDateRangeDto | null | undefined;
    }>();

    const emit = defineEmits<{
        (e: 'update:value', value: RelativeDateRangeDto | null): void;
    }>();

    const dayOptions = [3, 7, 14, 30, 60, 90];

    const isSelected = (preset: RelativeRangePreset, value: number | null = null) =>
        props.value?.preset === preset && (props.value?.value ?? null) === value;

    const set = (preset: RelativeRangePreset, value: number | null = null) => {
        emit('update:value', { preset, value });
    };

    const selectedDaysModel = computed<string | number>({
        get() {
            // show current value if LastNDays else default
            if (props.value?.preset !== RelativeRangePreset.LastNDays) return '';
            return props.value.value ?? dayOptions[0];
        },
        set(v) {
            if (v === '') return;

            const days = typeof v === 'string' ? parseInt(v, 10) : v;
            const safeDays = Number.isFinite(days) && days > 0 ? days : dayOptions[0];
            emit('update:value', { preset: RelativeRangePreset.LastNDays, value: safeDays });
        },
    });
</script>

<template>
    <AllFiltersItem>
        <template #icon><HourglassIcon class="size-5" /></template>
        {{ __('continuous-intervals') }}

        <template #content>
            <div class="px-4 pt-2 pb-6 border-t-[1px] border-neutral-300">
                <h4 class="Input-label !capitalize my-2">
                    {{ __('shortcuts') }}
                </h4>
                <div class="flex gap-1.5 flex-wrap">
                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.Today)"
                        @click="set(RelativeRangePreset.Today)"
                    >
                        {{ __('today') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.Yesterday)"
                        @click="set(RelativeRangePreset.Yesterday)"
                    >
                        {{ __('yesterday') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.ThisWeek)"
                        @click="set(RelativeRangePreset.ThisWeek)"
                    >
                        {{ __('this-week') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.LastWeek)"
                        @click="set(RelativeRangePreset.LastWeek)"
                    >
                        {{ __('last-week') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.MonthToDate)"
                        @click="set(RelativeRangePreset.MonthToDate)"
                    >
                        {{ __('this-month-to-date') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.LastMonth)"
                        @click="set(RelativeRangePreset.LastMonth)"
                    >
                        {{ __('last-month') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="isSelected(RelativeRangePreset.YearToDate)"
                        @click="set(RelativeRangePreset.YearToDate)"
                    >
                        {{ __('this-year-to-date') }}
                    </EchoButton>

                    <EchoButton
                        variant="secondary"
                        size="small"
                        :disabled="
                            props.value?.preset === RelativeRangePreset.LastNMonths &&
                            (props.value?.value ?? null) === 6
                        "
                        @click="set(RelativeRangePreset.LastNMonths, 6)"
                    >
                        {{ __('last-6-months') }}
                    </EchoButton>
                </div>
                <!-- Custom interval -->
                <div class="mt-6 border-t-[1px] border-gray-300">
                    <h4 class="Input-label !capitalize mt-4">
                        {{ __('custom-interval') }}
                    </h4>
                    <div class="flex items-center gap-3">
                        <label class="text-sm whitespace-nowrap text-neutral-700">
                            {{ __('number-of-days') }}
                        </label>

                        <SelectInput v-model="selectedDaysModel" class="!min-w-0 !w-20 text-center">
                            <option value="" disabled>
                                {{ __('choose') }}
                            </option>

                            <option v-for="d in dayOptions" :key="d" :value="d">
                                {{ d }}
                            </option>
                        </SelectInput>
                    </div>

                    <p
                        v-if="props.value?.preset === RelativeRangePreset.LastNDays"
                        class="text-sm text-neutral-500 mt-2"
                    >
                        {{ __('shows-data-for-last-days', { days: String(selectedDaysModel) }) }}
                    </p>
                </div>
            </div>
        </template>
    </AllFiltersItem>
</template>
